/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Card } from '@/components/ui/card';
import StaticPayPalButton from './static-paypal-button';
import { useTranslation } from 'react-i18next';

interface ExpressPaymentsProps {
    disabled?: boolean;
    /**
     * Layout orientation for the payment buttons
     * - 'horizontal': Responsive grid layout (2 cols mobile/tablet, 5 cols desktop)
     * - 'vertical': Stacked vertical layout (all buttons in single column)
     * @default 'horizontal'
     */
    layout?: 'horizontal' | 'vertical';
    /**
     * Position of the separator divider
     * - 'top': Displays separator above the payment buttons
     * - 'bottom': Displays separator below the payment buttons
     * @default 'bottom'
     */
    separatorPosition?: 'top' | 'bottom';
    /**
     * Custom text for the separator divider
     * @default 'Or'
     */
    separatorText?: string;
    /**
     * Whether to wrap the buttons in a Card with a title
     * @default true
     */
    showCard?: boolean;
}

/**
 * @feature-stub Express checkout buttons
 * @status stub — no backend integration
 *
 * These feature stubs are included to help accelerate your storefront
 * development. They give you a working UI scaffold — layout, styling,
 * interaction states, and accessibility — so you can focus on wiring up
 * the business logic and backend integrations that are unique to your
 * brand, rather than building everything from scratch.
 *
 * See docs/README-FEATURE-STUBS.md for the full list and guidance on
 * productionizing or removing stubs.
 *
 * Current behavior:
 *   Renders Apple Pay, Google Pay, Amazon Pay, PayPal, and Venmo buttons.
 *   Clicking any button triggers an alert() dialog. No payment is processed
 *   and no order is created.
 *
 * To productionize:
 *   Replace this component with your payment provider's SDK integration
 *   (e.g., Stripe, Adyen, Braintree). Each provider requires:
 *   1. Loading the provider's client-side SDK
 *   2. Initializing a payment session with your merchant credentials
 *   3. Collecting the payment token on approval
 *   4. Submitting the token through your checkout flow
 *      (SCAPI `/checkout/shopper-orders` or provider-specific endpoint)
 *
 * To remove:
 *   Delete this file and its related components (see docs/README-FEATURE-STUBS.md
 *   for the full file list), then remove the <ExpressPayments /> usage from:
 *   - src/components/checkout/checkout-form-page.tsx
 *   - src/components/product-cart-actions/index.tsx
 *
 * Remove this block once the real integration is wired up.
 */
export default function ExpressPayments({
    disabled = false,
    layout = 'horizontal',
    separatorPosition = 'bottom',
    separatorText = 'or continue below',
    showCard = true,
}: ExpressPaymentsProps) {
    const { t } = useTranslation('checkout');
    const handlePayPalClick = () => {
        if (!disabled) {
            // eslint-disable-next-line no-alert
            alert(
                'PayPal express checkout would be processed here. This would skip all form steps and go directly to payment confirmation.'
            );
        }
    };

    const gridClasses =
        layout === 'vertical' ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2';

    const separator = (
        <div className="relative flex items-center gap-[15px]">
            <div className="flex-1 h-px bg-separator" />
            <span
                className="text-sm font-normal leading-5 text-muted-foreground whitespace-nowrap"
                data-express-payments-separator-label="">
                {separatorText}
            </span>
            <div className="flex-1 h-px bg-separator" />
        </div>
    );

    const paymentButtons = (
        <>
            {/* PayPal Static Button — Ashley currently offers PayPal only for express checkout */}
            <StaticPayPalButton onClick={handlePayPalClick} disabled={disabled} />
        </>
    );

    return (
        <div className="space-y-4 px-8" data-testid="express-payments">
            {separatorPosition === 'top' && separator}

            {showCard ? (
                <Card className="flex flex-col items-center gap-3 p-6 shadow-none">
                    <p className="text-sm font-normal text-card-foreground">{t('expressPayments.title')}</p>
                    <div className={`${gridClasses} w-full`}>{paymentButtons}</div>
                </Card>
            ) : (
                <div className={`${gridClasses} w-full`}>{paymentButtons}</div>
            )}

            {separatorPosition === 'bottom' && separator}
        </div>
    );
}
